'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { CurrencySelectorContent, useCurrencySelection } from '@/components/currency-selector';
import { LanguageSwitcherContent, useLanguageSelection } from '@/components/language-switcher';
import { LocationSelectorContent, useLocationSelection } from '@/components/location-selector';
import { AnimatedButton } from '@/components/ui/animated-button';
import { DollarCircleIcon, Globe02Icon, Location01Icon } from '@/components/ui/icon/glyphs';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SegmentedControl, type SegmentedControlItem } from '@/components/ui/segmented-control';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { useCurrencyStore } from '@/stores/currency';
const USDC_LINK_PROPS = {
  href: 'https://www.circle.com/usdc',
  target: '_blank' as const,
  rel: 'noopener noreferrer',
  className: 'underline hover:text-foreground-primary'
};
export type LocaleLocationTab = 'language' | 'location' | 'currency';
export interface LocaleLocationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Tab to show when the modal opens. */
  initialTab?: LocaleLocationTab;
}

/**
 * Combined modal for language / location / currency selection. The tab toggle
 * is a `<SegmentedControl variant="chart-filled">` with prefixed icons; the
 * Update CTA lives in `<SilkSheet footer>` and dispatches to the active tab's
 * hook (matches the form/info modal pattern around `<ModalFooter>`).
 */
export function LocaleLocationModal(t0) {
  const $ = _c(72);
  const {
    open,
    onOpenChange,
    initialTab: t1
  } = t0;
  const initialTab = t1 === undefined ? "language" : t1;
  const t = useTranslations("header.settings");
  const tLanguage = useTranslations("language");
  const tLocation = useTranslations("location");
  const tCurrency = useTranslations("currency");
  const {
    isAuthenticated
  } = useAuth();
  const showLocationTab = isAuthenticated;
  const safeInitialTab = initialTab === "location" && !showLocationTab ? "language" : initialTab;
  const currency = useCurrencyStore(_temp);
  const [selectedTab, setSelectedTab] = useState(null);
  const activeTab = selectedTab === "location" && !showLocationTab ? safeInitialTab : selectedTab ?? safeInitialTab;
  const languageState = useLanguageSelection();
  const locationState = useLocationSelection();
  const currencyState = useCurrencySelection();
  const setLanguageSelected = languageState.setSelected;
  const setLocationSelected = locationState.setSelected;
  const setCurrencySelected = currencyState.setSelected;
  const currentLocale = languageState.currentLocale;
  const currentCurrency = currencyState.currentCurrency;
  let t2;
  let t3;
  if ($[0] !== currentCurrency || $[1] !== currentLocale || $[2] !== open || $[3] !== setCurrencySelected || $[4] !== setLanguageSelected || $[5] !== setLocationSelected) {
    t2 = () => {
      if (!open) {
        return;
      }
      setLanguageSelected(currentLocale);
      setLocationSelected(null);
      setCurrencySelected(currentCurrency);
    };
    t3 = [open, setLanguageSelected, setLocationSelected, setCurrencySelected, currentLocale, currentCurrency];
    $[0] = currentCurrency;
    $[1] = currentLocale;
    $[2] = open;
    $[3] = setCurrencySelected;
    $[4] = setLanguageSelected;
    $[5] = setLocationSelected;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== showLocationTab || $[9] !== t) {
    t4 = showLocationTab ? [{
      key: "location" as const,
      label: t("location"),
      icon: Location01Icon
    }] : [];
    $[8] = showLocationTab;
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t) {
    t5 = t("language");
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = {
      key: "language" as const,
      label: t5,
      icon: Globe02Icon
    };
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t) {
    t7 = t("currency");
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t7) {
    t8 = {
      key: "currency" as const,
      label: t7,
      icon: DollarCircleIcon
    };
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t4 || $[20] !== t6 || $[21] !== t8) {
    t9 = [...t4, t6, t8];
    $[19] = t4;
    $[20] = t6;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const items = t9;
  let t10;
  if ($[23] !== activeTab || $[24] !== currency || $[25] !== tCurrency || $[26] !== tLanguage || $[27] !== tLocation) {
    t10 = activeTab === "language" ? tLanguage("disclaimer") : activeTab === "location" ? tLocation("disclaimer") : tCurrency.rich("disclaimer", {
      currency,
      usdc: _temp2
    });
    $[23] = activeTab;
    $[24] = currency;
    $[25] = tCurrency;
    $[26] = tLanguage;
    $[27] = tLocation;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const description = t10;
  let t11;
  if ($[29] !== onOpenChange) {
    t11 = function close() {
      setSelectedTab(null);
      onOpenChange(false);
    };
    $[29] = onOpenChange;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const close = t11;
  let t12;
  if ($[31] !== onOpenChange) {
    t12 = function handleOpenChange(isOpen) {
      if (!isOpen) {
        setSelectedTab(null);
      }
      onOpenChange(isOpen);
    };
    $[31] = onOpenChange;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const handleOpenChange = t12;
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = function setActiveTab(tab) {
      setSelectedTab(tab);
    };
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const setActiveTab = t13;
  let t14;
  if ($[34] !== activeTab || $[35] !== close || $[36] !== currencyState || $[37] !== languageState || $[38] !== locationState) {
    t14 = function handleUpdate() {
      if (activeTab === "language") {
        languageState.apply(close);
      } else {
        if (activeTab === "location") {
          locationState.apply(close);
        } else {
          currencyState.apply(close);
        }
      }
    };
    $[34] = activeTab;
    $[35] = close;
    $[36] = currencyState;
    $[37] = languageState;
    $[38] = locationState;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const handleUpdate = t14;
  let t15;
  if ($[40] !== activeTab || $[41] !== locationState.loading || $[42] !== tCurrency || $[43] !== tLanguage || $[44] !== tLocation) {
    t15 = activeTab === "language" ? tLanguage("update_button") : activeTab === "location" ? locationState.loading ? tLocation("updating") : tLocation("update_button") : tCurrency("update_button");
    $[40] = activeTab;
    $[41] = locationState.loading;
    $[42] = tCurrency;
    $[43] = tLanguage;
    $[44] = tLocation;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  const ctaLabel = t15;
  const ctaDisabled = activeTab === "language" ? !languageState.hasChange : activeTab === "location" ? !locationState.hasChange || locationState.loading : !currencyState.hasChange;
  let t16;
  if ($[46] !== t) {
    t16 = t("locale_modal_title");
    $[46] = t;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  const t17 = activeTab === "location" && locationState.loading;
  let t18;
  if ($[48] !== ctaDisabled || $[49] !== ctaLabel || $[50] !== handleUpdate || $[51] !== t17) {
    t18 = <ModalFooter><AnimatedButton onClick={handleUpdate} disabled={ctaDisabled} loading={t17}>{ctaLabel}</AnimatedButton></ModalFooter>;
    $[48] = ctaDisabled;
    $[49] = ctaLabel;
    $[50] = handleUpdate;
    $[51] = t17;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = key => setActiveTab(key as LocaleLocationTab);
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== activeTab || $[55] !== items) {
    t20 = <SegmentedControl items={items} active={activeTab} onChange={t19} layoutId="locale-location-tabs" variant="chart-filled" size="md" fullWidth={true} asTabs={true} />;
    $[54] = activeTab;
    $[55] = items;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== activeTab || $[58] !== currencyState || $[59] !== languageState || $[60] !== locationState) {
    t21 = <div className="min-h-72">{activeTab === "language" ? <LanguageSwitcherContent state={languageState} /> : activeTab === "currency" ? <CurrencySelectorContent state={currencyState} /> : <LocationSelectorContent state={locationState} />}</div>;
    $[57] = activeTab;
    $[58] = currencyState;
    $[59] = languageState;
    $[60] = locationState;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== t20 || $[63] !== t21) {
    t22 = <div className="flex flex-col gap-4">{t20}{t21}</div>;
    $[62] = t20;
    $[63] = t21;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  let t23;
  if ($[65] !== description || $[66] !== handleOpenChange || $[67] !== open || $[68] !== t16 || $[69] !== t18 || $[70] !== t22) {
    t23 = <SilkSheet open={open} onOpenChange={handleOpenChange} variant="detached" size="md" keyboard={true} title={t16} description={description} footer={t18}>{t22}</SilkSheet>;
    $[65] = description;
    $[66] = handleOpenChange;
    $[67] = open;
    $[68] = t16;
    $[69] = t18;
    $[70] = t22;
    $[71] = t23;
  } else {
    t23 = $[71];
  }
  return t23;
}
function _temp2(chunks) {
  return <a {...USDC_LINK_PROPS}>{chunks}</a>;
}
function _temp(s) {
  return s.currency;
}